import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser, faPhone, faRupeeSign, faBalanceScale, faShoppingCart, faQrcode,
    faMoneyBill, faHistory, faExclamationTriangle, faFilter, faChevronDown,
    faChevronUp, faChevronLeft, faChevronRight, faSearch, faSpinner, faTimes,
    faSave, faPrint, faReceipt, faUsers, faEye, faCreditCard,
    faWallet, faUniversity, faCheckCircle, faExclamationCircle, faTag, faCalendarAlt,
    faList, faFileInvoice, faCheckSquare, faSquare, faPercent,
    faLandmark, faBarcode, faBuilding, faAddressCard, faIdCard, faCopy,
    faMoneyBillWave, faClock, faUserPlus, faBank, faInfoCircle, faBox,
    faArrowRight, faArrowLeft, faBoxes, faBolt, faChartLine, faTruck,
    faPen, faTools, faShieldAlt, faLaptopCode, faBriefcase, faPlane,
    faGraduationCap, faEllipsisH, faArrowDown, faArrowUp, faCalculator,
    faDollarSign, faCalendarCheck, faBriefcase as faBriefcaseIcon, faSort,
    faSortUp, faSortDown
} from '@fortawesome/free-solid-svg-icons';
import "../styles/CustomerAccount.css";

// ==============================================
// Debounce Function
// ==============================================
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// ==============================================
// Toast Notification Component (with ca- class names)
// ==============================================
const CaToastNotification = ({ toast, setToast, translations }) => {
    if (!toast.show) return null;
    return (
        <div className={`ca-toast-notification ${toast.type}`}>
            <div className="ca-toast-content">
                <div className="ca-toast-icon">
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationCircle} />
                </div>
                <div className="ca-toast-text">
                    <div className="ca-toast-message">{toast.message}</div>
                </div>
                <button className="ca-toast-close" onClick={() => setToast({ show: false })}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// Sortable Table Header Component (with ca- class names)
// ==============================================
const CaSortableHeader = ({ column, label, sortConfig, onSort, translations }) => {
    const isActive = sortConfig.key === column;
    return (
        <th onClick={() => onSort(column)} className="ca-sortable-header">
            {label}
            <span className="ca-sort-indicator">
                {isActive ? (
                    sortConfig.direction === 'asc' ?
                        <FontAwesomeIcon icon={faSortUp} /> :
                        <FontAwesomeIcon icon={faSortDown} />
                ) : (
                    <FontAwesomeIcon icon={faSort} />
                )}
            </span>
        </th>
    );
};

// ==============================================
// Salary Payment History Modal (with ca- class names)
// ==============================================
const CaSalaryHistoryModal = ({ show, onClose, shopId, employeeId, employeeName, translations }) => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'transaction_date', direction: 'desc' });

    useEffect(() => {
        if (show && shopId) loadTransactions();
    }, [show, shopId, employeeId]);

    const loadTransactions = async () => {
        setLoading(true);
        setError(null);
        try {
            const payload = { action: 'get_salary_payment_history', shop_id: parseInt(shopId), limit: 100 };
            if (employeeId) payload.employee_id = parseInt(employeeId);
            
            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const data = await response.json();
            if (data.status === 'success') setTransactions(data.data || []);
            else setError(data.message || 'Failed to load transactions');
        } catch (err) {
            setError('Network error loading transactions');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedData = (data) => {
        if (!data || !data.length) return [];
        const sorted = [...data];
        sorted.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            if (sortConfig.key === 'transaction_date') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            } else if (sortConfig.key === 'amount') {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            } else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
            }
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    };

    const formatCurrency = (a) => `Rs. ${parseFloat(a || 0).toFixed(2)}`;
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '';
    const sortedTransactions = getSortedData(transactions);
    const totalPaid = transactions.reduce((s, t) => s + parseFloat(t.amount || 0), 0);

    if (!show) return null;

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-history-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faHistory} /> {translations?.salaryHistory || 'Salary Payment History'}{employeeName && ` — ${employeeName}`}</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="ca-modal-body">
                    {loading ? (
                        <div className="ca-loading-state"><FontAwesomeIcon icon={faSpinner} spin /> {translations?.loading || 'Loading...'}</div>
                    ) : error ? (
                        <div className="ca-empty-state"><FontAwesomeIcon icon={faExclamationTriangle} /> {error}</div>
                    ) : transactions.length === 0 ? (
                        <div className="ca-empty-state">
                            <FontAwesomeIcon icon={faMoneyBillWave} />
                            <p>{translations?.noPayments || 'No salary payment history found'}</p>
                        </div>
                    ) : (
                        <div className="ca-table-container">
                            <table className="ca-table">
                                <thead>
                                    <tr>
                                        <CaSortableHeader column="transaction_date" label={translations?.date || 'Date'} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                        <th>{translations?.employee || 'Employee'}</th>
                                        <CaSortableHeader column="amount" label={translations?.amount || 'Amount'} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                        <th>{translations?.paymentMethod || 'Payment Method'}</th>
                                        <th>{translations?.reference || 'Reference'}</th>
                                        <th>{translations?.balanceAfter || 'Balance After'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedTransactions.map(tx => (
                                        <tr key={tx.id}>
                                            <td>{formatDate(tx.transaction_date)}</td>
                                            <td>{tx.employee_name}</td>
                                            <td className="ca-text-right ca-debit">-{formatCurrency(tx.amount)}</td>
                                            <td><span className="ca-payment-method">{tx.payment_method}</span></td>
                                            <td>{tx.reference_no || '-'}</td>
                                            <td className="ca-text-right">{formatCurrency(tx.balance_after)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="ca-summary-row">
                                        <td colSpan="2" className="ca-text-right"><strong>{translations?.totalPaid || 'Total Paid'}:</strong></td>
                                        <td className="ca-text-right ca-debit"><strong>-{formatCurrency(totalPaid)}</strong></td>
                                        <td colSpan="3"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
                <div className="ca-modal-footer">
                    <button className="ca-btn-secondary" onClick={onClose}>{translations?.close || 'Close'}</button>
                    <button className="ca-btn-primary" onClick={() => window.print()}>
                        <FontAwesomeIcon icon={faPrint} /> {translations?.print || 'Print'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Pending Salary Details Modal (with ca- class names)
// ==============================================
const CaPendingSalaryModal = ({ show, onClose, pendingSalaries, employeeName, onSelectSalaries, selectedSalaryIds, translations }) => {
    const [selectedIds, setSelectedIds] = useState(selectedSalaryIds || []);

    useEffect(() => {
        if (show) setSelectedIds(selectedSalaryIds || []);
    }, [show, selectedSalaryIds]);

    const toggleSelection = (salaryId) => {
        if (selectedIds.includes(salaryId)) {
            setSelectedIds(selectedIds.filter(id => id !== salaryId));
        } else {
            setSelectedIds([...selectedIds, salaryId]);
        }
    };

    const selectAll = () => {
        if (selectedIds.length === pendingSalaries.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(pendingSalaries.map(s => s.salaryid));
        }
    };

    const handleConfirm = () => {
        onSelectSalaries(selectedIds);
        onClose();
    };

    const totalSelected = pendingSalaries
        .filter(s => selectedIds.includes(s.salaryid))
        .reduce((sum, s) => sum + parseFloat(s.net_salary || 0), 0);

    const formatCurrency = (a) => `Rs. ${parseFloat(a || 0).toFixed(2)}`;
    const formatMonth = (date) => date ? new Date(date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long' }) : '';

    if (!show) return null;

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-pending-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faCalendarCheck} /> {translations?.pendingSalaries || 'Pending Salaries'} - {employeeName}</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="ca-modal-body">
                    {pendingSalaries.length === 0 ? (
                        <div className="ca-empty-state">
                            <FontAwesomeIcon icon={faCheckCircle} />
                            <p>{translations?.noPendingSalaries || 'No pending salaries for this employee'}</p>
                        </div>
                    ) : (
                        <>
                            <div className="ca-select-all-bar">
                                <button type="button" className="ca-select-all-btn" onClick={selectAll}>
                                    <FontAwesomeIcon icon={selectedIds.length === pendingSalaries.length ? faCheckSquare : faSquare} />
                                    {selectedIds.length === pendingSalaries.length ? (translations?.deselectAll || 'Deselect All') : (translations?.selectAll || 'Select All')}
                                </button>
                                <span className="ca-selected-count">{selectedIds.length} / {pendingSalaries.length} {translations?.monthsSelected || 'months selected'}</span>
                            </div>
                            
                            <div className="ca-pending-list">
                                {pendingSalaries.map(salary => (
                                    <div 
                                        key={salary.salaryid} 
                                        className={`ca-pending-item ${selectedIds.includes(salary.salaryid) ? 'ca-selected' : ''}`}
                                        onClick={() => toggleSelection(salary.salaryid)}
                                    >
                                        <div className="ca-pending-checkbox">
                                            <FontAwesomeIcon icon={selectedIds.includes(salary.salaryid) ? faCheckSquare : faSquare} />
                                        </div>
                                        <div className="ca-pending-details">
                                            <div className="ca-pending-month">
                                                <FontAwesomeIcon icon={faCalendarAlt} />
                                                {formatMonth(salary.salary_month)}
                                            </div>
                                            <div className="ca-pending-breakdown">
                                                <span>{translations?.basic || 'Basic'}: {formatCurrency(salary.basic_salary)}</span>
                                                {salary.allowances > 0 && <span>+ {translations?.allowances || 'Allow'}: {formatCurrency(salary.allowances)}</span>}
                                                {salary.bonus > 0 && <span>+ {translations?.bonus || 'Bonus'}: {formatCurrency(salary.bonus)}</span>}
                                                {salary.deductions > 0 && <span>- {translations?.deductions || 'Ded'}: {formatCurrency(salary.deductions)}</span>}
                                            </div>
                                        </div>
                                        <div className="ca-pending-amount">
                                            {formatCurrency(salary.net_salary)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {selectedIds.length > 0 && (
                                <div className="ca-selected-total">
                                    <strong>{translations?.totalToPay || 'Total to Pay'}:</strong>
                                    <span className="ca-total-amount">{formatCurrency(totalSelected)}</span>
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className="ca-modal-footer">
                    <button className="ca-btn-secondary" onClick={onClose}>{translations?.cancel || 'Cancel'}</button>
                    <button className="ca-btn-primary" onClick={handleConfirm} disabled={selectedIds.length === 0}>
                        <FontAwesomeIcon icon={faCheckCircle} /> {translations?.confirmSelection || 'Confirm Selection'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Main Pay Salary Component
// ==============================================
const PaySalary = () => {
    const { user, currentShop } = useAuth();

    const [shopId] = useState(() => {
        if (currentShop?.shop_id) return currentShop.shop_id;
        if (user?.shop_id) return user.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });

    // Employee sidebar state
    const [allEmployees, setAllEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Employee data state
    const [employeeData, setEmployeeData] = useState({
        employee: null,
        pending_salaries: [],
        transactions: [],
        total_due: 0,
        total_paid: 0,
        current_balance: 0,
        base_salary: 0
    });
    const [loading, setLoading] = useState(false);

    // Form state
    const [selectedSalaryIds, setSelectedSalaryIds] = useState([]);
    const [customAmount, setCustomAmount] = useState('');
    const [paymentMode, setPaymentMode] = useState('salary_based');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
    const [referenceNo, setReferenceNo] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    // UI state
    const [activeTab, setActiveTab] = useState('form');
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showPendingModal, setShowPendingModal] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [sortConfig, setSortConfig] = useState({ key: 'transaction_date', direction: 'desc' });

    const currentLanguage = localStorage.getItem('ourzest_language') || 'en';
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Translations
    const translations = {
        paySalary: 'Pay Salary',
        manageSalary: 'Record salary payments and manage employee dues',
        employees: 'Employees',
        searchEmployees: 'Search employees by name or phone...',
        noEmployeesFound: 'No employees found',
        selectEmployee: 'Select an Employee',
        selectEmployeeMessage: 'Select an employee from the list to view their salary details and record payments',
        employee: 'Employee',
        baseSalary: 'Base Salary',
        balanceDue: 'Balance Due',
        perMonth: 'per month',
        totalDue: 'Total Due',
        totalPaid: 'Total Paid',
        paymentHistory: 'Payment History',
        pendingSalaries: 'Pending Salaries',
        paySalaryBtn: 'Pay Salary',
        paymentForm: 'Payment Form',
        transactionHistory: 'Transaction History',
        paymentMode: 'Payment Mode',
        payPendingSalaries: 'Pay Pending Salaries',
        customAmount: 'Custom Amount',
        noSalariesSelected: 'No salaries selected. Click "Pending Salaries" to select which months to pay.',
        monthsSelected: 'month(s) selected',
        total: 'Total',
        selectSalaries: 'Select Salaries',
        paymentMethod: 'Payment Method',
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        cheque: 'Cheque',
        upi: 'UPI',
        transactionDetails: 'Transaction Details',
        amount: 'Amount',
        paymentDate: 'Payment Date',
        referenceNo: 'Reference Number',
        notes: 'Notes',
        cancel: 'Cancel',
        paySalarySubmit: 'Pay Salary',
        saving: 'Saving...',
        availableBalance: 'Available Balance',
        date: 'Date',
        type: 'Type',
        reference: 'Reference',
        balanceAfter: 'Balance After',
        salaryPayment: 'Salary Payment',
        noTransactions: 'No transaction history found',
        loading: 'Loading...',
        success: 'Success',
        error: 'Error',
        salaryPaid: 'Salary payment recorded successfully',
        paymentFailed: 'Failed to record payment',
        networkError: 'Network error occurred',
        validAmount: 'Amount must be greater than 0',
        amountExceedsBalance: 'Amount exceeds current balance due',
        selectEmployeeRequired: 'Please select an employee',
        close: 'Close',
        print: 'Print',
        salaryHistory: 'Salary Payment History',
        noPayments: 'No salary payment history found',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
        basic: 'Basic',
        allowances: 'Allow',
        bonus: 'Bonus',
        deductions: 'Ded',
        totalToPay: 'Total to Pay',
        confirmSelection: 'Confirm Selection'
    };

    const formatCurrency = (a) => `Rs. ${parseFloat(a || 0).toFixed(2)}`;
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '';

    const showToast = (type, message) => {
        setToast({ show: true, type, message });
        setTimeout(() => setToast({ show: false }), 3000);
    };

    // Load all employees
    const loadAllEmployees = useCallback(async () => {
        if (!shopId || !user?.username) return;
        setLoadingEmployees(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_employees_for_salary',
                    username: user.username,
                    shop_id: parseInt(shopId)
                })
            });
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const data = await response.json();
            if (data.status === 'success') {
                setAllEmployees(data.data || []);
            } else {
                console.error('Employees load failed:', data.message);
                setAllEmployees([]);
            }
        } catch (err) {
            console.error('Error loading employees:', err);
            setAllEmployees([]);
        } finally {
            setLoadingEmployees(false);
        }
    }, [shopId, user?.username]);

    useEffect(() => {
        if (shopId && user?.username) loadAllEmployees();
    }, [shopId, user?.username, loadAllEmployees]);

    // Load employee salary details
    const loadEmployeeSalaryDetails = useCallback(async (employeeId) => {
        if (!shopId) return;
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_employee_salary_details',
                    employee_id: parseInt(employeeId),
                    shop_id: parseInt(shopId),
                    username: user?.username
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setEmployeeData(data.data);
                setSelectedSalaryIds([]);
                setCustomAmount('');
                setPaymentMode('salary_based');
                setErrors({});
            } else {
                showToast('error', data.message || 'Failed to load employee details');
            }
        } catch (err) {
            console.error('Error loading employee details:', err);
            showToast('error', 'Network error');
        } finally {
            setLoading(false);
        }
    }, [shopId, user?.username]);

    // Select employee
    const selectEmployee = (employee) => {
        setSelectedEmployee(employee);
        loadEmployeeSalaryDetails(employee.employeeid);
        setActiveTab('form');
    };

    // Handle salary selection from modal
    const handleSalarySelection = (selectedIds) => {
        setSelectedSalaryIds(selectedIds);
        const totalSelected = employeeData.pending_salaries
            .filter(s => selectedIds.includes(s.salaryid))
            .reduce((sum, s) => sum + parseFloat(s.net_salary || 0), 0);
        setCustomAmount(totalSelected.toString());
    };

    // Calculate amount based on payment mode
    const getAmountToPay = () => {
        return parseFloat(customAmount) || 0;
    };

    const validateForm = () => {
        const newErrors = {};
        const amount = getAmountToPay();
        
        if (amount <= 0) {
            newErrors.amount = translations.validAmount;
        } else if (amount > employeeData.current_balance && employeeData.current_balance > 0) {
            newErrors.amount = translations.amountExceedsBalance;
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const resetForm = () => {
        setSelectedSalaryIds([]);
        setCustomAmount('');
        setPaymentMode('salary_based');
        setPaymentMethod('cash');
        setReferenceNo('');
        setNotes('');
        setErrors({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm() || saving) return;
        
        setSaving(true);
        try {
            const username = user?.username || localStorage.getItem('username');
            const transactionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const amount = getAmountToPay();
            
            const payload = {
                action: 'record_salary_payment',
                employee_id: selectedEmployee.employeeid,
                shop_id: parseInt(shopId),
                amount: amount,
                salary_month: new Date().toISOString().split('T')[0],
                payment_method: paymentMethod,
                transaction_date: transactionDate,
                username: username,
                notes: notes,
                reference_no: referenceNo,
                selected_salary_ids: paymentMode === 'salary_based' ? selectedSalaryIds : [],
                idempotency_key: transactionId
            };
            
            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', translations.salaryPaid);
                resetForm();
                await loadEmployeeSalaryDetails(selectedEmployee.employeeid);
                await loadAllEmployees();
            } else {
                showToast('error', data.message || translations.paymentFailed);
            }
        } catch (err) {
            console.error('Error recording payment:', err);
            showToast('error', translations.networkError);
        } finally {
            setSaving(false);
        }
    };

    // Handle sort
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sorted data
    const getSortedData = (data) => {
        if (!data || !data.length) return [];
        const sorted = [...data];
        sorted.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            if (sortConfig.key === 'transaction_date') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            } else if (sortConfig.key === 'amount') {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            } else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
            }
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    };

    // Filter employees for sidebar
    const filteredEmployees = allEmployees.filter(emp => {
        if (!searchTerm) return true;
        const sl = searchTerm.toLowerCase();
        return (
            emp.employee_name?.toLowerCase().includes(sl) ||
            emp.phone?.includes(searchTerm) ||
            emp.position?.toLowerCase().includes(sl)
        );
    });

    const paymentMethods = [
        { value: 'cash', label: translations.cash, icon: faMoneyBillWave },
        { value: 'bank_transfer', label: translations.bankTransfer, icon: faLandmark },
        { value: 'cheque', label: translations.cheque, icon: faUniversity },
        { value: 'upi', label: translations.upi, icon: faQrcode }
    ];

    const totalPaid = employeeData.total_paid || 0;
    const totalDue = employeeData.total_due || 0;
    const currentBalance = employeeData.current_balance || 0;
    const baseSalary = employeeData.base_salary || 0;
    const sortedTransactions = getSortedData(employeeData.transactions || []);

    return (
        <div className={`ca-container ${isRTL ? 'ca-rtl' : ''}`}>
            <CaToastNotification toast={toast} setToast={setToast} translations={translations} />

            <CaSalaryHistoryModal
                show={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                shopId={shopId}
                employeeId={selectedEmployee?.employeeid}
                employeeName={selectedEmployee?.employee_name}
                translations={translations}
            />

            <CaPendingSalaryModal
                show={showPendingModal}
                onClose={() => setShowPendingModal(false)}
                pendingSalaries={employeeData.pending_salaries || []}
                employeeName={selectedEmployee?.employee_name}
                onSelectSalaries={handleSalarySelection}
                selectedSalaryIds={selectedSalaryIds}
                translations={translations}
            />

            {/* Header */}
            <div className="ca-header">
                <div className="ca-header-content">
                    <div className="ca-header-icon">
                        <FontAwesomeIcon icon={faMoneyBillWave} />
                    </div>
                    <div className="ca-header-text">
                        <h1>{translations.paySalary}</h1>
                        <p>{translations.manageSalary}</p>
                        {currentShop && (
                            <div className="ca-shop-badge">
                                <FontAwesomeIcon icon={faTag} /> {currentShop.shop_name}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Layout */}
            <div className="ca-main-layout">
                {/* Left Sidebar - Employees List */}
                <div className={`ca-sidebar ${sidebarCollapsed ? 'ca-collapsed' : ''}`}>
                    <div className="ca-sidebar-header">
                        <button className="ca-sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                            <FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft} />
                        </button>
                        {!sidebarCollapsed && (
                            <div className="ca-sidebar-title">
                                <FontAwesomeIcon icon={faUsers} />
                                <span>{translations.employees}</span>
                            </div>
                        )}
                    </div>

                    {!sidebarCollapsed && (
                        <>
                            <div className="ca-sidebar-search">
                                <div className="ca-search-wrapper">
                                    <FontAwesomeIcon icon={faSearch} className="ca-search-icon" />
                                    <input
                                        type="text"
                                        placeholder={translations.searchEmployees}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {loadingEmployees && <FontAwesomeIcon icon={faSpinner} spin className="ca-search-spinner" />}
                                </div>
                            </div>

                            <div className="ca-customer-list">
                                {loadingEmployees ? (
                                    <div className="ca-loading-customers">
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        <span>{translations.loading}</span>
                                    </div>
                                ) : filteredEmployees.length === 0 ? (
                                    <div className="ca-no-customers">
                                        <FontAwesomeIcon icon={faUsers} />
                                        <span>{translations.noEmployeesFound}</span>
                                    </div>
                                ) : (
                                    filteredEmployees.map(employee => (
                                        <div
                                            key={employee.employeeid}
                                            className={`ca-customer-item ${selectedEmployee?.employeeid === employee.employeeid ? 'ca-active' : ''}`}
                                            onClick={() => selectEmployee(employee)}
                                        >
                                            <div className="ca-customer-content">
                                                <div className="ca-customer-name">
                                                    <FontAwesomeIcon icon={faUser} className="ca-item-icon" />
                                                    <span className="ca-customer-name-text">{employee.employee_name}</span>
                                                </div>
                                                {employee.position && (
                                                    <div className="ca-customer-phone">
                                                        <FontAwesomeIcon icon={faBriefcaseIcon} size="xs" /> {employee.position}
                                                    </div>
                                                )}
                                                {employee.phone && (
                                                    <div className="ca-customer-phone">
                                                        <FontAwesomeIcon icon={faPhone} size="xs" /> {employee.phone}
                                                    </div>
                                                )}
                                                <div className="ca-customer-stats">
                                                    <span className="ca-debit-stat">{translations.baseSalary}: {formatCurrency(employee.base_salary)}</span>
                                                    <span className={`ca-customer-balance ${employee.current_balance > 0 ? 'ca-positive' : ''}`}>
                                                        {translations.balanceDue}: {formatCurrency(employee.current_balance)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Main Content */}
                <div className="ca-main-content">
                    {selectedEmployee ? (
                        <div className="ca-account-content">
                            {/* Summary Cards */}
                            <div className="ca-summary-cards">
                                <div className="ca-summary-card">
                                    <div className="ca-card-icon"><FontAwesomeIcon icon={faUser} /></div>
                                    <div className="ca-card-content">
                                        <div className="ca-card-label">{translations.employee}</div>
                                        <div className="ca-card-value">{selectedEmployee.employee_name}</div>
                                        {selectedEmployee.position && (
                                            <div className="ca-card-subvalue">
                                                <FontAwesomeIcon icon={faBriefcaseIcon} size="xs" /> {selectedEmployee.position}
                                            </div>
                                        )}
                                        {selectedEmployee.phone && (
                                            <div className="ca-card-subvalue">
                                                <FontAwesomeIcon icon={faPhone} size="xs" /> {selectedEmployee.phone}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="ca-summary-card">
                                    <div className="ca-card-icon"><FontAwesomeIcon icon={faDollarSign} /></div>
                                    <div className="ca-card-content">
                                        <div className="ca-card-label">{translations.baseSalary}</div>
                                        <div className="ca-card-value">{formatCurrency(baseSalary)}</div>
                                        <div className="ca-card-subvalue">{translations.perMonth}</div>
                                    </div>
                                </div>

                                <div className={`ca-summary-card ca-balance-card ${currentBalance > 0 ? 'ca-balance-positive' : ''}`}>
                                    <div className="ca-card-icon"><FontAwesomeIcon icon={faBalanceScale} /></div>
                                    <div className="ca-card-content">
                                        <div className="ca-card-label">{translations.balanceDue}</div>
                                        <div className="ca-card-value">{formatCurrency(currentBalance)}</div>
                                        <div className="ca-card-subvalue">
                                            {translations.totalDue}: {formatCurrency(totalDue)} | {translations.totalPaid}: {formatCurrency(totalPaid)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="ca-action-buttons">
                                <button className="ca-btn-secondary" onClick={() => setShowHistoryModal(true)}>
                                    <FontAwesomeIcon icon={faHistory} /> {translations.paymentHistory}
                                </button>
                                {employeeData.pending_salaries?.length > 0 && (
                                    <button className="ca-btn-secondary ca-pending-btn" onClick={() => setShowPendingModal(true)}>
                                        <FontAwesomeIcon icon={faCalendarCheck} /> {translations.pendingSalaries} ({employeeData.pending_salaries.length})
                                    </button>
                                )}
                                <button className="ca-btn-primary ca-pay-btn" onClick={() => setActiveTab('form')}>
                                    <FontAwesomeIcon icon={faSave} /> {translations.paySalaryBtn}
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className="ca-tabs">
                                <button className={`ca-tab-btn ${activeTab === 'form' ? 'ca-active' : ''}`} onClick={() => setActiveTab('form')}>
                                    <FontAwesomeIcon icon={faMoneyBillWave} /> {translations.paymentForm}
                                </button>
                                <button className={`ca-tab-btn ${activeTab === 'transactions' ? 'ca-active' : ''}`} onClick={() => setActiveTab('transactions')}>
                                    <FontAwesomeIcon icon={faHistory} /> {translations.transactionHistory}
                                </button>
                            </div>

                            {/* Tab Content */}
                            <div className="ca-tab-content">
                                {/* Form Tab */}
                                {activeTab === 'form' && (
                                    <form onSubmit={handleSubmit} className="ca-transaction-form">
                                        {/* Payment Mode Selection */}
                                        <div className="ca-form-section">
                                            <h3 className="ca-section-title">
                                                <FontAwesomeIcon icon={faCalculator} /> {translations.paymentMode}
                                            </h3>
                                            <div className="ca-payment-mode-toggle">
                                                <button
                                                    type="button"
                                                    className={`ca-mode-btn ${paymentMode === 'salary_based' ? 'ca-active' : ''}`}
                                                    onClick={() => {
                                                        setPaymentMode('salary_based');
                                                        if (selectedSalaryIds.length > 0) {
                                                            const total = employeeData.pending_salaries
                                                                .filter(s => selectedSalaryIds.includes(s.salaryid))
                                                                .reduce((sum, s) => sum + parseFloat(s.net_salary || 0), 0);
                                                            setCustomAmount(total.toString());
                                                        }
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faCalendarCheck} /> {translations.payPendingSalaries}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`ca-mode-btn ${paymentMode === 'custom' ? 'ca-active' : ''}`}
                                                    onClick={() => {
                                                        setPaymentMode('custom');
                                                        setCustomAmount('');
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faRupeeSign} /> {translations.customAmount}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Salary Selection Info */}
                                        {paymentMode === 'salary_based' && (
                                            <div className="ca-form-section">
                                                <div className="ca-info-card">
                                                    <FontAwesomeIcon icon={faInfoCircle} />
                                                    <div className="ca-info-content">
                                                        {selectedSalaryIds.length === 0 ? (
                                                            <span>{translations.noSalariesSelected}</span>
                                                        ) : (
                                                            <span>{selectedSalaryIds.length} {translations.monthsSelected}. {translations.total}: {formatCurrency(parseFloat(customAmount) || 0)}</span>
                                                        )}
                                                    </div>
                                                    <button type="button" className="ca-btn-small" onClick={() => setShowPendingModal(true)}>
                                                        <FontAwesomeIcon icon={faList} /> {translations.selectSalaries}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Payment Method */}
                                        <div className="ca-form-section">
                                            <h3 className="ca-section-title">
                                                <FontAwesomeIcon icon={faCreditCard} /> {translations.paymentMethod}
                                            </h3>
                                            <div className="ca-payment-methods-grid">
                                                {paymentMethods.map(m => (
                                                    <button
                                                        key={m.value}
                                                        type="button"
                                                        className={`ca-payment-method-btn ${paymentMethod === m.value ? 'ca-active' : ''}`}
                                                        onClick={() => setPaymentMethod(m.value)}
                                                        disabled={saving}
                                                    >
                                                        <FontAwesomeIcon icon={m.icon} />
                                                        <span>{m.label}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Transaction Details */}
                                        <div className="ca-form-section">
                                            <h3 className="ca-section-title">
                                                <FontAwesomeIcon icon={faFileInvoice} /> {translations.transactionDetails}
                                            </h3>
                                            <div className="ca-form-grid">
                                                {/* Amount */}
                                                <div className={`ca-form-group ${errors.amount ? 'has-error' : ''}`}>
                                                    <div className="ca-input-container">
                                                        <FontAwesomeIcon icon={faRupeeSign} className="ca-input-icon" />
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={customAmount}
                                                            onChange={(e) => setCustomAmount(e.target.value)}
                                                            placeholder=" "
                                                            dir="ltr"
                                                            disabled={paymentMode === 'salary_based' && selectedSalaryIds.length > 0}
                                                        />
                                                        <label className="ca-input-label">{translations.amount} <span className="ca-required">*</span></label>
                                                    </div>
                                                    {errors.amount && (
                                                        <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.amount}</div>
                                                    )}
                                                    <div className="ca-help-text">
                                                        <FontAwesomeIcon icon={faInfoCircle} /> {translations.availableBalance}:&nbsp;
                                                        <strong className={currentBalance > 0 ? 'ca-text-danger' : 'ca-text-success'}>
                                                            {formatCurrency(currentBalance)}
                                                        </strong>
                                                    </div>
                                                </div>

                                                {/* Date */}
                                                <div className="ca-form-group">
                                                    <div className="ca-input-container">
                                                        <FontAwesomeIcon icon={faCalendarAlt} className="ca-input-icon" />
                                                        <input
                                                            type="date"
                                                            value={transactionDate}
                                                            onChange={(e) => setTransactionDate(e.target.value)}
                                                            dir="ltr"
                                                        />
                                                        <label className="ca-input-label">{translations.paymentDate}</label>
                                                    </div>
                                                </div>

                                                {/* Reference */}
                                                <div className="ca-form-group">
                                                    <div className="ca-input-container">
                                                        <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                                                        <input
                                                            type="text"
                                                            value={referenceNo}
                                                            onChange={(e) => setReferenceNo(e.target.value)}
                                                            placeholder=" "
                                                            dir={isRTL ? 'rtl' : 'ltr'}
                                                        />
                                                        <label className="ca-input-label">{translations.referenceNo}</label>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Notes */}
                                            <div className="ca-form-group ca-full-width">
                                                <div className="ca-input-container ca-textarea-container">
                                                    <FontAwesomeIcon icon={faReceipt} className="ca-input-icon" />
                                                    <textarea
                                                        value={notes}
                                                        onChange={(e) => setNotes(e.target.value)}
                                                        placeholder=" "
                                                        rows="3"
                                                        dir={isRTL ? 'rtl' : 'ltr'}
                                                    />
                                                    <label className="ca-input-label">{translations.notes}</label>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Form Actions */}
                                        <div className="ca-form-actions">
                                            <button type="button" className="ca-btn-secondary" onClick={resetForm}>
                                                {translations.cancel}
                                            </button>
                                            <button type="submit" className="ca-btn-primary ca-pay-btn" disabled={saving}>
                                                {saving ? (
                                                    <><FontAwesomeIcon icon={faSpinner} spin /> {translations.saving}</>
                                                ) : (
                                                    <><FontAwesomeIcon icon={faSave} /> {translations.paySalarySubmit}</>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {/* History Tab */}
                                {activeTab === 'transactions' && (
                                    <div className="ca-table-container">
                                        <table className="ca-table">
                                            <thead>
                                                <tr>
                                                    <CaSortableHeader column="transaction_date" label={translations.date} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                    <th>{translations.type}</th>
                                                    <CaSortableHeader column="amount" label={translations.amount} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                    <th>{translations.paymentMethod}</th>
                                                    <th>{translations.reference}</th>
                                                    <th>{translations.balanceAfter}</th>
                                                    <th>{translations.notes}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedTransactions.map((t, i) => (
                                                    <tr key={t.id || i}>
                                                        <td>{formatDate(t.transaction_date)}</td>
                                                        <td>
                                                            <span className="ca-transaction-type ca-debit">
                                                                <FontAwesomeIcon icon={faArrowDown} /> {translations.salaryPayment}
                                                            </span>
                                                        </td>
                                                        <td className="ca-text-right ca-debit">-{formatCurrency(t.amount)}</td>
                                                        <td><span className="ca-payment-method">{t.payment_method}</span></td>
                                                        <td>{t.reference_no || '-'}</td>
                                                        <td className="ca-text-right">{formatCurrency(t.balance_after)}</td>
                                                        <td>{t.notes || '-'}</td>
                                                    </tr>
                                                ))}
                                                {(!employeeData.transactions || employeeData.transactions.length === 0) && (
                                                    <tr>
                                                        <td colSpan="7" className="ca-text-center ca-no-data">
                                                            {translations.noTransactions}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                            {employeeData.transactions && employeeData.transactions.length > 0 && (
                                                <tfoot>
                                                    <tr className="ca-summary-row">
                                                        <td colSpan="2" className="ca-text-right"><strong>{translations.totalPaid}:</strong></td>
                                                        <td className="ca-text-right ca-debit"><strong>-{formatCurrency(totalPaid)}</strong></td>
                                                        <td colSpan="4"></td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="ca-no-selected">
                            <FontAwesomeIcon icon={faUsers} className="ca-empty-icon" />
                            <h3>{translations.selectEmployee}</h3>
                            <p>{translations.selectEmployeeMessage}</p>
                            <p className="ca-hint-text">
                                <FontAwesomeIcon icon={faDollarSign} /> {translations.employees} {translations.showBaseSalaryInfo || 'show their base salary and current balance due'}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Loading Overlay */}
            {loading && (
                <div className="ca-loading-overlay">
                    <div className="ca-loading-content">
                        <div className="ca-loading-spinner"></div>
                        <p>{translations.loading}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaySalary;